/** @format */

import { CloseOutlined, LeftOutlined, LockOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Drawer, Popconfirm } from "antd";
import moment from "moment";
import React, { useState } from "react";
import { TModalitaPresenze } from "../../../../redux-modules/declarations/maps/turniSettimana";
import {
  TGiornoPresenze,
  TGiustificativoGiorno,
  TPermessiPresenze,
  TPersonaGiornata,
  TPersonaPresenze,
  TPresenzaVista,
  TTurnoPresenze,
} from "../../../../redux-modules/declarations/maps/presenzeVista";
import {
  azioniPresenza,
  azioniSegnalazione,
  contestiDelGiorno,
  dataEstesa,
  descrizioneContrattoGiorno,
  durataDiscorsiva,
  durataTesto,
  eStraordinario,
  formatMinutiBrevi,
  etichettaStatoTurno,
  EVENTI_STORICO,
  giornoBreve,
  livelloPresenza,
  maiuscola,
  metaGiustificativo,
  nomeCompleto,
  ora,
  plurale,
  quando,
  STATI_TIMEBANK,
  testoGiustificativo,
  testoGiustificativoConDurata,
  testoOrigine,
  testoScostamento,
  titoloSegnalazione,
  titoloTurno,
  TRATTINO,
  TUnitaPresenze,
  uscitaProposta,
} from "../format";
import { oreAsse, TFasciaMinuti } from "../linea";
import { PannelloStile, Pulsanti, Segnalazione } from "../styles";
import { azioniPresenze, STATO_RICHIESTA, TEsitoAzione } from "../usePresenze";
import { PulsantiAzione, TRisorseAzioni } from "./Azioni";
import { AvatarPersona } from "./AvatarPersona";
import { DettaglioSegnalazione } from "./DettaglioSegnalazione";
import { LineaGiorno } from "./LineaGiorno";

interface Props {
  aperto: boolean;
  data: string;
  /** dati della persona nel giorno; null se nel giorno non ha turni, presenze o giustificativi */
  persona: TPersonaGiornata | null;
  /** intestazione quando la persona non ha niente nel giorno */
  personaNota: TPersonaPresenze | null;
  adesso: string;
  modalita: TModalitaPresenze;
  permessi: TPermessiPresenze;
  unita: TUnitaPresenze;
  fascia: TFasciaMinuti;
  passoAsse: number;
  risorse: TRisorseAzioni;
  onChiudi: () => void;
  onGiorno: (data: string) => void;
  /** dalla Giornata: porta al mese della persona */
  onMese?: (persona: TPersonaPresenze, data: string) => void;
  /** dal Mese: porta alla Giornata di quel giorno */
  onGiornata?: (persona: TPersonaPresenze, data: string) => void;
  onNuovaPresenza: (persona: TPersonaPresenze, data: string) => void;
  onNuovaRichiesta: (persona: TPersonaPresenze, data: string) => void;
}

/** Pannello laterale della giornata di una persona: in alto turni e timbrature. */
export const PannelloGiornata = ({
  aperto,
  data,
  persona,
  personaNota,
  adesso,
  modalita,
  permessi,
  unita,
  fascia,
  passoAsse,
  risorse,
  onChiudi,
  onGiorno,
  onMese,
  onGiornata,
  onNuovaPresenza,
  onNuovaRichiesta,
}: Props): JSX.Element => {
  const chi: TPersonaPresenze | null = persona || personaNota;
  const ieri = moment(data).subtract(1, "day").format("YYYY-MM-DD");
  const domani = moment(data).add(1, "day").format("YYYY-MM-DD");

  const titolo = chi ? (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <AvatarPersona persona={chi} grande />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 700 }}>{nomeCompleto(chi)}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#5A6174" }}>
          {chi.gruppo ? `${chi.gruppo}, ` : ""}
          {dataEstesa(data)}
        </div>
      </div>
    </div>
  ) : (
    "Giornata"
  );

  const piede = chi ? (
    <Pulsanti>
      {permessi.crea && (
        <Button size="small" onClick={(): void => onNuovaPresenza(chi, data)}>
          Nuova presenza
        </Button>
      )}
      {permessi.gestisci_richieste && (
        <Button size="small" onClick={(): void => onNuovaRichiesta(chi, data)}>
          Nuova richiesta
        </Button>
      )}
      {onMese && (
        <Button size="small" type="link" onClick={(): void => onMese(chi, data)}>
          Mese di {chi.nome}
        </Button>
      )}
      {onGiornata && (
        <Button size="small" type="link" onClick={(): void => onGiornata(chi, data)}>
          Vai alla Giornata
        </Button>
      )}
    </Pulsanti>
  ) : null;

  return (
    <Drawer
      visible={aperto}
      placement="right"
      width={480}
      onClose={onChiudi}
      title={titolo}
      closeIcon={<CloseOutlined aria-label="Chiudi" />}
      footer={piede}
      bodyStyle={{ padding: "16px 18px 20px" }}
    >
      <PannelloStile>
        <nav className="naviga" aria-label="Giorno">
          <Button size="small" icon={<LeftOutlined />} onClick={(): void => onGiorno(ieri)}>
            {maiuscola(giornoBreve(ieri))}
          </Button>
          <Button size="small" onClick={(): void => onGiorno(domani)}>
            {maiuscola(giornoBreve(domani))} <RightOutlined />
          </Button>
        </nav>

        {persona ? (
          <ContenutoGiornata
            key={`${persona.id}:${data}`}
            persona={persona}
            giorno={persona.giorno}
            adesso={adesso}
            modalita={modalita}
            permessi={permessi}
            unita={unita}
            fascia={fascia}
            passoAsse={passoAsse}
            risorse={risorse}
            onNuovaRichiesta={onNuovaRichiesta}
          />
        ) : (
          <p className="attenuato">
            Nessun turno, nessuna timbratura e nessun giustificativo in questo giorno.
          </p>
        )}
      </PannelloStile>
    </Drawer>
  );
};

interface PropsContenuto {
  persona: TPersonaGiornata;
  giorno: TGiornoPresenze;
  adesso: string;
  modalita: TModalitaPresenze;
  permessi: TPermessiPresenze;
  unita: TUnitaPresenze;
  fascia: TFasciaMinuti;
  passoAsse: number;
  risorse: TRisorseAzioni;
  onNuovaRichiesta: (persona: TPersonaPresenze, data: string) => void;
}

const ContenutoGiornata = ({
  persona,
  giorno,
  adesso,
  modalita,
  permessi,
  unita,
  fascia,
  passoAsse,
  risorse,
  onNuovaRichiesta,
}: PropsContenuto): JSX.Element => {
  const contesti = contestiDelGiorno(persona, giorno);
  const { minuti_lavorati: lavorate, minuti_previsti: previste } = giorno.totali;
  const turni = [...giorno.turni].sort((a, b) => a.inizio.localeCompare(b.inizio));
  const idTurni = turni.map(t => t.id);
  const senzaTurno = giorno.presenze.filter(p => !p.turno_id || idTurni.indexOf(p.turno_id) < 0);

  return (
    <>
      <section className="sezione" aria-label="Turni e timbrature">
        <div className="testa-sezione">
          <h3>Turni e timbrature</h3>
          {(previste > 0 || lavorate > 0) && (
            <span className="totale">
              {durataTesto(lavorate, unita)}{" "}
              <span>
                {previste > 0 ? `di ${durataTesto(previste, unita)} previste` : "lavorate"}
              </span>
            </span>
          )}
        </div>
        <div className="contratto">
          Contratto del giorno: {descrizioneContrattoGiorno(persona.contratto)}
        </div>
        <Bandierine giorno={giorno} />
        <div className="mini">
          <div className="asse-mini" aria-hidden="true">
            {oreAsse(fascia, passoAsse * 2).map((h, i, tutte) => (
              <span
                key={h.etichetta}
                className={i === 0 ? "prima" : i === tutte.length - 1 ? "ultima" : undefined}
                style={{ left: `${h.sinistra}%` }}
              >
                {h.etichetta}
              </span>
            ))}
          </div>
          <LineaGiorno giorno={giorno} fascia={fascia} adesso={adesso} pixel={420} compatta />
        </div>

        {turni.map(t => (
          <SchedaTurno
            key={t.id}
            turno={t}
            persona={persona}
            giorno={giorno}
            modalita={modalita}
            permessi={permessi}
            unita={unita}
            risorse={risorse}
          />
        ))}
        {senzaTurno.map(p => (
          <SchedaPresenza
            key={p.id}
            presenza={p}
            persona={persona}
            giorno={giorno}
            permessi={permessi}
            unita={unita}
            risorse={risorse}
          />
        ))}
        {!turni.length && !senzaTurno.length && (
          <span className="attenuato">Nessun turno e nessuna timbratura in questo giorno.</span>
        )}
      </section>

      <section className="sezione" aria-label="Giustificativi">
        <div className="testa-sezione">
          <h3>Giustificativi</h3>
          {permessi.gestisci_richieste && (
            <Button
              size="small"
              type="text"
              onClick={(): void => onNuovaRichiesta(persona, giorno.data)}
            >
              Nuova richiesta
            </Button>
          )}
        </div>
        {giorno.giustificativi.map(g => (
          <SchedaGiustificativo
            key={g.richiesta_id}
            giustificativo={g}
            giorno={giorno.data}
            persona={persona}
            bloccato={giorno.bloccato}
            puoGestire={permessi.gestisci_richieste}
            onEsito={risorse.onEsito}
          />
        ))}
        {!giorno.giustificativi.length && (
          <span className="attenuato">Nessun giustificativo in questo giorno.</span>
        )}
      </section>

      {contesti.length > 0 && (
        <section className="sezione" aria-label="Da gestire">
          <h3>Da gestire</h3>
          {contesti.map(c => {
            const { pulsanti, nota } = azioniSegnalazione(c, permessi);
            return (
              <Segnalazione key={c.chiave} className={c.livello}>
                <div className="testa">
                  <strong>{titoloSegnalazione(c, modalita)}</strong>
                  {c.data !== giorno.data && <span className="giorno">{giornoBreve(c.data)}</span>}
                </div>
                <DettaglioSegnalazione contesto={c} modalita={modalita} unita={unita} />
                {nota && <span className="nota">{nota}</span>}
                <PulsantiAzione
                  pulsanti={pulsanti}
                  risorse={risorse}
                  persona={persona}
                  presenza={c.presenza}
                  turno={c.turno}
                  giorno={c.data}
                  uscitaProposta={uscitaProposta(c)}
                />
              </Segnalazione>
            );
          })}
        </section>
      )}

      {permessi.storico && <Storico giorno={giorno} />}
    </>
  );
};

const Bandierine = ({ giorno }: { giorno: TGiornoPresenze }): JSX.Element | null => {
  const voci: JSX.Element[] = [];
  if (giorno.totali.anomalie > 0) {
    voci.push(
      <span key="a" className="bandierina rosso">
        {plurale(giorno.totali.anomalie, "anomalia", "anomalie")}
      </span>,
    );
  }
  if (giorno.totali.avvisi > 0) {
    voci.push(
      <span key="v" className="bandierina ambra">
        {plurale(giorno.totali.avvisi, "avviso", "avvisi")}
      </span>,
    );
  }
  if (giorno.bloccato) {
    voci.push(
      <span key="b" className="bandierina grigio">
        <LockOutlined /> Giorno bloccato
      </span>,
    );
  }
  if (giorno.festivita) {
    voci.push(
      <span key="f" className="bandierina festa">
        Festività: {giorno.festivita}
      </span>,
    );
  }
  if (!giorno.previsto) {
    voci.push(
      <span key="p" className="bandierina grigio">
        Giorno non previsto dal contratto
      </span>,
    );
  }
  return voci.length ? <div className="bandierine">{voci}</div> : null;
};

interface PropsScheda {
  persona: TPersonaGiornata;
  giorno: TGiornoPresenze;
  permessi: TPermessiPresenze;
  unita: TUnitaPresenze;
  risorse: TRisorseAzioni;
}

/** Righe di una timbratura collegata a un turno: orari, scostamenti e banca ore. */
const righePresenza = (
  p: TPresenzaVista,
  giorno: TGiornoPresenze,
  modalita: TModalitaPresenze,
  unita: TUnitaPresenze,
): { k: string; v: string }[] => {
  const righe: { k: string; v: string }[] = [];
  righe.push({
    k: "Timbratura",
    v: `${quando(p.entrata, giorno.data)} ${TRATTINO} ${
      p.uscita ? quando(p.uscita, giorno.data) : "in corso"
    }, ${testoOrigine(p.origine)}`,
  });
  righe.push({
    k: "Durata",
    v: p.aperta ? `${durataDiscorsiva(p.minuti)} finora` : durataDiscorsiva(p.minuti),
  });
  if (p.corretta) {
    righe.push({
      k: "Timbrata",
      v: `${ora(p.timbrata.entrata)} ${TRATTINO} ${
        p.timbrata.uscita ? ora(p.timbrata.uscita) : "in corso"
      }, poi corretta`,
    });
  }
  const tb = p.timebank;
  if (tb && eStraordinario(p)) {
    // lo straordinario è già uno scostamento: conta solo se è approvato
    let stato =
      tb.stato === "in_attesa"
        ? "da approvare"
        : tb.stato === "approvata"
        ? "approvato"
        : "rifiutato";
    if (tb.stato !== "in_attesa" && tb.approvata_da) stato += ` da ${tb.approvata_da}`;
    righe.push({ k: "Stato", v: stato });
  } else if (tb) {
    righe.push({ k: "Ingresso", v: testoScostamento(tb.ingresso_minuti, "ingresso", unita) });
    if (p.uscita)
      righe.push({ k: "Uscita", v: testoScostamento(tb.uscita_minuti, "uscita", unita) });
    if (tb.permessi_minuti > 0)
      righe.push({ k: "Permessi", v: formatMinutiBrevi(tb.permessi_minuti, unita) });
    let stato = STATI_TIMEBANK[tb.stato]?.testo.toLowerCase() || tb.stato;
    if (tb.stato !== "in_attesa" && tb.approvata_da) stato += ` da ${tb.approvata_da}`;
    if (tb.regola) stato += ` (regola ${tb.regola})`;
    righe.push({
      k: modalita === "banca_ore" ? "Banca ore" : "Scostamento",
      v: `${durataTesto(tb.delta_minuti, unita, true)}, ${stato}`,
    });
  }
  if (
    p.tipo.codice_sistema &&
    p.tipo.codice_sistema !== "ORDINARIO" &&
    !eStraordinario(p) &&
    p.tipo.nome
  ) {
    righe.push({ k: "Tipo", v: p.tipo.nome });
  }
  if (p.sede) righe.push({ k: "Sede", v: p.sede });
  return righe;
};

let contatoreSchede = 0;

interface PropsSchedaRichiudibile {
  titolo: React.ReactNode;
  pillole?: React.ReactNode;
  /** righe visibili anche a scheda chiusa */
  riassunto?: React.ReactNode;
  /** false se non c'è altro da mostrare: la testata non si apre */
  apribile?: boolean;
  classe?: string;
  children?: React.ReactNode;
}

/**
 * Scheda del pannello che si apre e si chiude dalla testata (punti 6 e 7). Chiusa mostra titolo,
 * stato e riassunto; aperta anche i dettagli e i pulsanti. Parte chiusa a ogni apertura del giorno.
 */
const SchedaRichiudibile = ({
  titolo,
  pillole,
  riassunto,
  apribile = true,
  classe,
  children,
}: PropsSchedaRichiudibile): JSX.Element => {
  const [aperta, setAperta] = useState(false);
  const [idCorpo] = useState(() => `scheda-pannello-${++contatoreSchede}`);
  const classi = ["scheda", apribile ? "richiudibile" : "", aperta ? "aperta" : "", classe || ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classi}>
      {apribile ? (
        <button
          type="button"
          className="testa-scheda"
          aria-expanded={aperta}
          aria-controls={idCorpo}
          onClick={(): void => setAperta(a => !a)}
        >
          <span className="titolo-scheda">
            <RightOutlined className="freccia" aria-hidden="true" />
            {titolo}
          </span>
          {pillole && <span className="pillole">{pillole}</span>}
        </button>
      ) : (
        <div className="testa-scheda">
          <span className="titolo-scheda">{titolo}</span>
          {pillole && <span className="pillole">{pillole}</span>}
        </div>
      )}
      {riassunto}
      {apribile && (
        <div id={idCorpo} className="corpo-scheda" hidden={!aperta}>
          {children}
        </div>
      )}
    </div>
  );
};

/** Segnalazioni del turno o delle sue timbrature: per il segno "Da gestire" sulla testata chiusa. */
const livelloTurno = (
  giorno: TGiornoPresenze,
  turnoId: number,
  presenze: number[],
): "anomalia" | "avviso" | null => {
  const sue = giorno.segnalazioni.filter(
    s =>
      s.shift_planning_id === turnoId ||
      (s.presence_id !== null && presenze.indexOf(s.presence_id) >= 0),
  );
  if (sue.some(s => s.livello === "anomalia")) return "anomalia";
  return sue.length ? "avviso" : null;
};

const SchedaTurno = ({
  turno,
  persona,
  giorno,
  modalita,
  permessi,
  unita,
  risorse,
}: PropsScheda & { turno: TTurnoPresenze; modalita: TModalitaPresenze }): JSX.Element => {
  const collegate = giorno.presenze.filter(p => p.turno_id === turno.id);
  const etichetta = etichettaStatoTurno(turno);
  // il turno dello straordinario nasce dall'approvazione: non c'è una durata "da timbrare"
  const straordinario = !turno.visibile || collegate.some(eStraordinario);
  const conDurata = turno.lavorativo && turno.minuti > 0 && !straordinario;
  // come i giustificativi: "Turno 09:00–13:30, 4 ore e 30 min"
  let titolo = titoloTurno(turno);
  if (conDurata) titolo += `, ${durataDiscorsiva(turno.minuti)}`;
  if (turno.giorno_prima) titolo += ", del giorno prima";
  if (turno.sede) titolo += `, ${turno.sede}`;

  let senzaTimbratura = "nessuna collegata";
  if (turno.stato === "da_svolgere") senzaTimbratura = "turno da svolgere";
  if (turno.stato === "coperto") senzaTimbratura = etichetta.testo.toLowerCase();
  if (turno.stato === "mancata_timbratura" && !turno.inseribile && turno.inseribile_dalle) {
    senzaTimbratura = "nessuna collegata, turno in corso";
  }

  // con la pillola rossa del turno (non timbrato) il segno sarebbe un doppione
  const livello = livelloTurno(
    giorno,
    turno.id,
    collegate.map(p => p.id),
  );
  const daGestire = livello && etichetta.colore !== "rosso";

  return (
    <SchedaRichiudibile
      titolo={titolo}
      apribile={turno.lavorativo || collegate.length > 0}
      pillole={
        <>
          {daGestire && (
            <span className={`pillola ${livello === "anomalia" ? "rosso" : "ambra"}`}>
              Da gestire
            </span>
          )}
          <span className={`pillola ${etichetta.colore}`}>{etichetta.testo}</span>
        </>
      }
    >
      {turno.lavorativo && !collegate.length && (
        <dl className="dl">
          <dt>Timbratura</dt>
          <dd>{senzaTimbratura}</dd>
        </dl>
      )}
      {collegate.map(p => (
        <React.Fragment key={p.id}>
          <dl className="dl">
            {righePresenza(p, giorno, modalita, unita).map(r => (
              <React.Fragment key={r.k}>
                <dt>{r.k}</dt>
                <dd>{r.v}</dd>
              </React.Fragment>
            ))}
          </dl>
          <PulsantiAzione
            pulsanti={azioniPresenza(p, permessi)}
            risorse={risorse}
            persona={persona}
            presenza={p}
            turno={turno}
            giorno={giorno.data}
          />
        </React.Fragment>
      ))}
    </SchedaRichiudibile>
  );
};

const SchedaPresenza = ({
  presenza: p,
  persona,
  giorno,
  permessi,
  risorse,
}: PropsScheda & { presenza: TPresenzaVista }): JSX.Element => {
  const livello = livelloPresenza(giorno, p.id);
  const titolo = p.giorno_prima
    ? "Presenza iniziata il giorno prima"
    : p.turno_id
    ? "Presenza di un turno del giorno prima"
    : "Presenza senza turno";
  return (
    <SchedaRichiudibile
      titolo={titolo}
      pillole={
        <>
          {livello && (
            <span className={`pillola ${livello === "anomalia" ? "rosso" : "ambra"}`}>
              {livello === "anomalia" ? "Anomalia" : "Avviso"}
            </span>
          )}
          {!livello && p.aperta && <span className="pillola verde">In corso</span>}
        </>
      }
      riassunto={
        <dl className="dl">
          <dt>Orari</dt>
          <dd>
            {quando(p.entrata, giorno.data)} {TRATTINO}{" "}
            {p.uscita ? quando(p.uscita, giorno.data) : "in corso"}
          </dd>
        </dl>
      }
    >
      <dl className="dl">
        <dt>Durata</dt>
        <dd>{p.aperta ? `${durataDiscorsiva(p.minuti)} finora` : durataDiscorsiva(p.minuti)}</dd>
        <dt>Origine</dt>
        <dd>{testoOrigine(p.origine)}</dd>
        {p.tipo.nome && p.tipo.codice_sistema !== "ORDINARIO" && (
          <>
            <dt>Tipo</dt>
            <dd>{p.tipo.nome}</dd>
          </>
        )}
      </dl>
      <PulsantiAzione
        pulsanti={azioniPresenza(p, permessi)}
        risorse={risorse}
        persona={persona}
        presenza={p}
        turno={null}
        giorno={p.entrata.substr(0, 10)}
      />
    </SchedaRichiudibile>
  );
};

interface PropsGiustificativo {
  giustificativo: TGiustificativoGiorno;
  /** giorno mostrato ("YYYY-MM-DD"): per dire di quale giorno è un permesso di un notturno */
  giorno: string;
  persona: TPersonaPresenze;
  bloccato: boolean;
  puoGestire: boolean;
  onEsito: (esito: TEsitoAzione) => void;
}

/** Giustificativo del giorno: si accetta, si rifiuta o si annulla da qui (a scheda aperta). */
const SchedaGiustificativo = ({
  giustificativo: g,
  giorno,
  persona,
  bloccato,
  puoGestire,
  onEsito,
}: PropsGiustificativo): JSX.Element => {
  const [inCorso, setInCorso] = useState<number | null>(null);
  const cambia = (stato: number): Promise<void> => {
    setInCorso(stato);
    return azioniPresenze.statoRichiesta(g.richiesta_id, persona.id, stato).then(esito => {
      setInCorso(null);
      onEsito(esito);
    });
  };
  const inAttesa = g.stato === "in_attesa";

  return (
    <SchedaRichiudibile
      classe={inAttesa ? "in-attesa" : undefined}
      titolo={
        <span className="giustificativo">
          <span className="pallino" style={{ background: g.colore || "#E59E6E" }} />
          {testoGiustificativoConDurata(g, giorno)}
        </span>
      }
      pillole={
        <span className={`pillola ${inAttesa ? "ambra" : "grigio"}`}>
          {inAttesa ? "In attesa" : "Accettata"}
        </span>
      }
    >
      <span className="meta">{metaGiustificativo(g)}</span>
      {g.nota && <span className="nota-dipendente">Nota: {g.nota}</span>}
      {puoGestire && bloccato && (
        <span className="meta">Il giorno è bloccato: non si può modificare.</span>
      )}
      {puoGestire && !bloccato && (
        <Pulsanti>
          {inAttesa && (
            <>
              <Button
                size="small"
                type="primary"
                loading={inCorso === STATO_RICHIESTA.accettata}
                disabled={inCorso !== null}
                onClick={(): Promise<void> => cambia(STATO_RICHIESTA.accettata)}
              >
                Accetta
              </Button>
              <Button
                size="small"
                loading={inCorso === STATO_RICHIESTA.rifiutata}
                disabled={inCorso !== null}
                onClick={(): Promise<void> => cambia(STATO_RICHIESTA.rifiutata)}
              >
                Rifiuta
              </Button>
            </>
          )}
          {!inAttesa && (
            <Popconfirm
              title={`Annullare ${testoGiustificativo(g, giorno).toLowerCase()}?`}
              okText="Annulla la richiesta"
              okButtonProps={{ danger: true }}
              cancelText="No"
              onConfirm={(): Promise<void> => cambia(STATO_RICHIESTA.annullata)}
            >
              <Button size="small" danger loading={inCorso === STATO_RICHIESTA.annullata}>
                Annulla richiesta
              </Button>
            </Popconfirm>
          )}
        </Pulsanti>
      )}
    </SchedaRichiudibile>
  );
};

/** Cosa è stato registrato sulle presenze del giorno, in ordine di tempo. */
const Storico = ({ giorno }: { giorno: TGiornoPresenze }): JSX.Element => {
  const eventi = giorno.presenze
    .flatMap(p => (p.storico || []).map((e, i) => ({ ...e, chiave: `${p.id}:${i}` })))
    .sort((a, b) => (a.quando || "").localeCompare(b.quando || ""));

  return (
    <section className="sezione" aria-label="Storico">
      <h3>Storico: cosa è stato registrato</h3>
      {eventi.length ? (
        <ol className="storico">
          {eventi.map(e => (
            <li key={e.chiave}>
              <span className="quando">
                {e.quando
                  ? e.quando.substr(0, 10) === giorno.data
                    ? ora(e.quando)
                    : `${moment(e.quando.substr(0, 10)).format("ddd D")}, ${ora(e.quando)}`
                  : TRATTINO}
              </span>
              <span className="cosa">
                <b>{EVENTI_STORICO[e.evento] || maiuscola(e.evento)}</b>
                {e.origine ? `, ${testoOrigine(e.origine)}` : ""}
                {e.nota ? `: ${e.nota}` : ""}
                {e.modifica ? ` (${e.modifica})` : ""}
                {e.autore ? `, da ${e.autore}` : ""}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <span className="attenuato">Nessun evento registrato sulle presenze di questo giorno.</span>
      )}
    </section>
  );
};
